'use client';

import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

/**
 * Standardized input styling for in-sheet CRUD forms. Apply to raw
 * `<input>`, `<textarea>`, and `<select>` elements inside {@link FormField}.
 */
export const sheetFormInputClass =
  'bg-background w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50';

/**
 * Dark-glass variant of {@link sheetFormInputClass}, for forms rendered on a
 * dark liquid-glass panel (e.g. the profile sheet). Matches the person
 * page's ContactInfoForm inputs.
 */
export const sheetFormInputDarkClass =
  'w-full rounded-lg border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/40 transition-colors focus:bg-white/15 focus:ring-2 focus:ring-primary focus:outline-none';

/**
 * Opt-in dark styling for a form scaffold + its fields. Set `dark` once on
 * {@link SheetFormPage}; {@link FormField} reads it from context. Default is
 * light, so existing consumers are untouched.
 */
const SheetFormDarkContext = createContext(false);

/**
 * Labeled form-field wrapper. Handles the label, required-marker, optional
 * help text below the control, and an optional tooltip hint rendered as a
 * small info icon beside the label.
 */
export function FormField({
  label,
  required,
  help,
  tooltip,
  children,
}: {
  label: string;
  required?: boolean;
  help?: ReactNode;
  /** Short hint shown in a tooltip anchored to an info icon next to the label. */
  tooltip?: string;
  children: ReactNode;
}) {
  const dark = useContext(SheetFormDarkContext);
  return (
    <div className="flex flex-col gap-1">
      <label
        className={
          dark
            ? 'flex items-center gap-1.5 text-xs font-medium tracking-wide text-white/50 uppercase'
            : 'text-foreground flex items-center gap-1.5 text-sm font-medium'
        }
      >
        <span>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </span>
        {tooltip && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={tooltip}
                  className={
                    dark
                      ? 'inline-flex items-center justify-center text-white/40 transition-colors hover:text-white/70'
                      : 'text-muted-foreground hover:text-foreground inline-flex items-center justify-center transition-colors'
                  }
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </label>
      {children}
      {help && (
        <p className={dark ? 'text-xs text-white/40' : 'text-muted-foreground text-xs'}>{help}</p>
      )}
    </div>
  );
}

/**
 * Scaffold for an in-sheet CRUD form.
 *
 * Action-button treatment follows a Linear/Superhuman pattern:
 *  - Single primary action, right-aligned on desktop, full-width pinned on mobile.
 *  - No Cancel button — Back chevron, backdrop click, Esc, and the drag
 *    handle on mobile sheets already cover dismiss.
 *  - ⌘↵ / Ctrl↵ submits the form; a `kbd` hint sits inside the button on
 *    desktop so the shortcut is discoverable.
 *  - Disabled while invalid (40% opacity, brightness fades in when valid);
 *    spinner replaces the label when submitting so width stays locked.
 *
 * Pair with {@link FormField} + {@link sheetFormInputClass} for individual
 * form fields.
 */
export function SheetFormPage({
  description,
  helperText,
  children,
  onSubmit,
  submitLabel = 'Save',
  isSubmitting,
  isValid = true,
  dark = false,
}: {
  description?: ReactNode;
  helperText?: ReactNode;
  children: ReactNode;
  onSubmit: () => void;
  submitLabel?: string;
  isSubmitting?: boolean;
  isValid?: boolean;
  /** Dark-glass styling for forms on a dark panel (profile sheet). */
  dark?: boolean;
}) {
  const submitRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isSubmitting) {
        e.preventDefault();
        submitRef.current?.click();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSubmitting]);

  return (
    <SheetFormDarkContext.Provider value={dark}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!isSubmitting && isValid) onSubmit();
        }}
        className="flex flex-col gap-5 p-4 md:p-6"
      >
        {description && (
          <p className={dark ? 'text-sm text-white/60' : 'text-muted-foreground text-sm'}>
            {description}
          </p>
        )}
        <div className="flex flex-col gap-4">{children}</div>

        {/* Action row
          Mobile: full-width pinned primary button, helperText stacks above.
          Desktop: helperText left-aligned, compact primary button right with ⌘↵ hint. */}
        <div
          className={`mt-1 flex flex-col gap-3 border-t pt-4 md:flex-row md:items-center md:justify-between md:gap-4 ${
            dark ? 'border-white/10' : 'border-border/60'
          }`}
        >
          {helperText ? (
            <p className={dark ? 'text-xs text-white/40' : 'text-muted-foreground text-xs'}>
              {helperText}
            </p>
          ) : (
            <span className="hidden md:block" />
          )}
          <button
            ref={submitRef}
            type="submit"
            disabled={isSubmitting || !isValid}
            title={!isValid ? 'Fill the required fields to continue' : undefined}
            className="bg-primary focus-visible:ring-primary/50 inline-flex h-12 w-full items-center justify-center gap-2 px-4 text-sm font-semibold text-white shadow-sm transition-opacity duration-200 hover:brightness-[1.03] focus-visible:ring-2 focus-visible:outline-none active:brightness-[0.97] disabled:cursor-not-allowed disabled:opacity-40 md:h-9 md:w-auto"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <span>{submitLabel}</span>
                <kbd className="ml-1 hidden rounded bg-white/15 px-1.5 py-0.5 font-mono text-[10px] font-medium tracking-wide md:inline-flex">
                  ⌘↵
                </kbd>
              </>
            )}
          </button>
        </div>
      </form>
    </SheetFormDarkContext.Provider>
  );
}
